import { Provider } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { CUSTOM_WIDGET_PREVIEW_URL_RESOLVER } from '@app/finxone-web-frontend/app/Modules/shared/widgets/custom-widget/custom-widget-preview-url';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { CardManagementService } from '@app/finxone-web-frontend/app/lib/services/card-management-service/card-management-service';
import { CompaniesHouseService } from '@app/finxone-web-frontend/app/lib/services/companies-house-service/companies-house.service';
import { DeviceTypeService } from '@app/finxone-web-frontend/app/lib/services/device-type/device-type.service';
import { KeycloakCustomService } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-custom.service';
import { MetadataService } from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.service';
import { MultiPartFormHolderService } from '@app/finxone-web-frontend/app/lib/services/multipart-holder/multipart-form-holder.service';
import { RecurringPaymentsService } from '@app/finxone-web-frontend/app/lib/services/recurring-payments-service/recurring-payments.service';
import { SavingsAccountService } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.service';
import { SocketService } from '@app/finxone-web-frontend/app/lib/services/socket-service/socket-service.service';
import { ProviderClass } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { ActiveConfigService } from '../../libs/services/active-config/active-config.service';
import { MockAccountService } from '../../modules/ui-builder/libs/mock-services/account-service.service';
import { MockCardManagementService } from '../../modules/ui-builder/libs/mock-services/card-management-service';
import { MockCompaniesHouseService } from '../../modules/ui-builder/libs/mock-services/companies-house.service';
import { MockConfigService } from '../../modules/ui-builder/libs/mock-services/config-service.service';
import { mockFormService } from '../../modules/ui-builder/libs/mock-services/form-service.service';
import { MockKeycloakCustomService } from '../../modules/ui-builder/libs/mock-services/keycloak-custom.service';
import { MockMetadataService } from '../../modules/ui-builder/libs/mock-services/metadata-service.service';
import { MockRecurringPaymentsService } from '../../modules/ui-builder/libs/mock-services/recurring-payments.service';
import { MockDeviceTypeService } from '../../modules/ui-builder/libs/mock-services/mock-device-type.service';
import { MockSavingsAccountService } from '../../modules/ui-builder/libs/mock-services/savings-account.service';
import { MockSocketService } from '../../modules/ui-builder/libs/mock-services/socket-service.service';
import { ProjectsState } from '../state/project.state';
import { ApiService } from '../services/api-service/api.service';
import { ConfigService } from '../services/config-service/config.service';
import { RouteService } from '../services/route-service/route.service';
import { SystemConfigService } from '../services/system-config/system-config.service';
import { StateService } from '@app/finxone-web-frontend/app/lib/services/state-service/state.service';

export function createMockEnvironmentProviders(
  systemConfigService: SystemConfigService,
  store: Store,
  registry: MatIconRegistry,
  domSanitizer: DomSanitizer,
  routeService: RouteService,
  activeConfigService: ActiveConfigService = {} as ActiveConfigService,
  apiservice: ApiService = {} as ApiService,
  stateService: StateService,
): Provider[] {
  return [
    {
      provide: Store,
      useValue: store,
    },
    {
      provide: ConfigService,
      useValue: new MockConfigService(
        systemConfigService,
        activeConfigService,
        apiservice,
        registry,
        domSanitizer,
        undefined as unknown as Store,
        routeService,
        stateService,
      ),
    },
    {
      provide: MultiPartFormHolderService,
      useValue: mockFormService,
    },
    {
      provide: AccountService,
      useFactory: (store: Store) => new MockAccountService(store),
      deps: [Store],
    },
    {
      provide: SavingsAccountService,
      useValue: new MockSavingsAccountService(),
    },
    {
      provide: SocketService,
      useValue: new MockSocketService(),
    },
    {
      provide: MetadataService,
      useValue: new MockMetadataService(),
    },
    {
      provide: KeycloakCustomService,
      useValue: new MockKeycloakCustomService(),
    },
    {
      provide: ProviderClass,
      useValue: null,
    },
    {
      provide: DeviceTypeService,
      useValue: new MockDeviceTypeService(),
    },
    {
      provide: RouterOutlet,
      useValue: {},
    },
    {
      provide: CardManagementService,
      useFactory: (store: Store) => {
        return new MockCardManagementService(store);
      },
      deps: [Store],
    },
    {
      provide: RecurringPaymentsService,
      useFactory: (store: Store) => new MockRecurringPaymentsService(store),
      deps: [Store],
    },
    {
      // Canvas-only: a placed custom widget's companies.search / companies.get run
      // through the same host bridge the deployed app uses, so the canvas has to
      // answer them without a company-registry call.
      provide: CompaniesHouseService,
      useValue: new MockCompaniesHouseService(),
    },
    {
      // Canvas-only: a placed widget's bundleUrl points at /assets/custom-widgets/, which only
      // a deployed project's web-frontend serves. The builder is a different app on a different
      // origin, so the preview frame is pointed at the provisioner's bundle route instead.
      provide: CUSTOM_WIDGET_PREVIEW_URL_RESOLVER,
      useValue: createCustomWidgetPreviewUrlResolver(apiservice, store),
    },
  ];
}

/**
 * Maps a placed widget to the provisioner route that serves its runner.
 *
 * With an active project, the grant-aware activated-bundle route is used: it serves the
 * activation's pinned version with connect-src = the granted origins, so a network-granted
 * widget behaves in the canvas exactly as it will in the deployed app. Without one (or
 * before the base URL is known) it falls back to the version-pinned preview route, whose
 * response header pins connect-src 'none'. Nothing this returns is ever written into
 * workflow_config — the component resolves it per render.
 */
export function createCustomWidgetPreviewUrlResolver(apiService: ApiService, store?: Store) {
  return {
    resolve: ({ widgetId, version }: { widgetId: string; version: string }): string | undefined => {
      const baseUrl = apiService?.baseUrl?.replace(/\/+$/, '');
      if (!baseUrl) {
        return undefined;
      }
      const projectId = store?.selectSnapshot(ProjectsState.getActiveProject)?.id;
      if (projectId) {
        return `${baseUrl}/projects/${encodeURIComponent(projectId)}/custom-widgets/${encodeURIComponent(
          widgetId,
        )}/activated-bundle/runner.html`;
      }
      return `${baseUrl}/custom-widgets/${encodeURIComponent(widgetId)}/versions/${encodeURIComponent(
        version,
      )}/bundle/runner.html`;
    },
  };
}
